import { computed, nextTick, type Ref } from "vue";
import type { FormContext } from "vee-validate";
import type {
  DeliverySelectValue,
  CapSelectValue,
  SelectTypeValue,
  DeliveryGroupSelectValue,
  ListDataAreaDeliveryIdResist,
} from "../types";
import {
  DeliveryGroupCreateInput,
  DeliveryGroupUpdateInput,
} from "@/generated/graphql";

/**
 * composableへ渡す依存をまとめた型。
 */
interface UseDeliverySettingGroupFormParams {
  processFlag: Ref<number>;
  mode: Ref<string>;
  groupFormRef: Ref<FormContext | null>;
  deliveryGroupCreateInput: DeliveryGroupCreateInput;
  deliveryGroupUpdateInput: DeliveryGroupUpdateInput;
  deliverySelectValues: Ref<DeliverySelectValue>;
  deliveryGroupSelectValues: Ref<DeliveryGroupSelectValue>;
  selectTypeSelectValues: Ref<SelectTypeValue>;
  selectGroupCapSelectValues: Ref<CapSelectValue>;
  selectListCheckbox: Ref<ListDataAreaDeliveryIdResist[]>;
  wordList: Record<string, string>;
  isValidNumberValue: (value: unknown) => boolean;
}

/**
 * 画面ロジックを扱う composable。
 */
export const useDeliverySettingGroupForm = ({
  processFlag,
  mode,
  groupFormRef,
  deliveryGroupCreateInput,
  deliveryGroupUpdateInput,
  deliverySelectValues,
  deliveryGroupSelectValues,
  selectTypeSelectValues,
  selectGroupCapSelectValues,
  selectListCheckbox,
  wordList,
  isValidNumberValue,
}: UseDeliverySettingGroupFormParams) => {
  const fieldName = (key: string): string => {
    return wordList[key] || key;
  };

  // グループ便編集フォームのバリデーション状態を計算
  const isGroupFormValid = computed((): boolean => {
    // 新規登録時のチェック
    if (processFlag.value === 0) {
      // 新規の場合は配達グループ名称が必須
      if (selectTypeSelectValues.value.index === "0") {
        if (!deliveryGroupCreateInput.name || deliveryGroupCreateInput.name.trim() === "") {
          return false;
        }
      } else {
        // 既存の場合は配達グループが選択されている必要がある
        if (!deliveryGroupSelectValues.value?.group_code) {
          return false;
        }
      }
      // 配達便が選択されている必要がある
      if (
        deliverySelectValues.value?.delivery_id === null ||
        deliverySelectValues.value?.delivery_id === undefined
      ) {
        return false;
      }
      // 対象エリア・便が選択されている必要がある
      if (selectListCheckbox.value.length === 0) {
        return false;
      }
    } else if (processFlag.value === 1) {
      // 編集時は配達グループ名称が必須
      if (!deliveryGroupUpdateInput.name || deliveryGroupUpdateInput.name.trim() === "") {
        return false;
      }
    }

    // キャップ数設定のバリデーション
    if (selectGroupCapSelectValues.value.index === 0) {
      // 全曜日の場合は基本キャップ数が必須
      return isValidNumberValue(deliveryGroupCreateInput.max_deliverable_unit);
    }

    // 曜日ごとの場合は全曜日のキャップ数が必須
    return [
      deliveryGroupCreateInput.group_cap_mon,
      deliveryGroupCreateInput.group_cap_tue,
      deliveryGroupCreateInput.group_cap_wed,
      deliveryGroupCreateInput.group_cap_thu,
      deliveryGroupCreateInput.group_cap_fri,
      deliveryGroupCreateInput.group_cap_sat,
      deliveryGroupCreateInput.group_cap_sun,
    ].every((value) => isValidNumberValue(value));
  });

  // グループ便編集画面初期表示時およびキャップ設定切り替え時に vee-validate の内部値を同期する。
  const syncGroupFormValues = async (): Promise<void> => {
    if (mode.value !== "group" || processFlag.value === 2) {
      return;
    }
    await nextTick();
    const form = groupFormRef.value;
    if (!form) {
      return;
    }

    // 新規/既存ラジオボタン
    if (processFlag.value === 0) {
      form.setFieldValue(
        fieldName("lbl_cur_deliverable_display_method"),
        selectTypeSelectValues.value.index,
        false
      );
    }

    // 配達グループ名称
    if (processFlag.value === 0) {
      if (selectTypeSelectValues.value.index === "0") {
        form.setFieldValue(
          fieldName("lbl_delivery_group_name"),
          deliveryGroupCreateInput.name,
          false
        );
      } else {
        form.setFieldValue(
          fieldName("lbl_delivery_group_name"),
          deliveryGroupSelectValues.value,
          false
        );
      }
      // 配達便
      form.setFieldValue(
        fieldName("lbl_delivery_id"),
        deliverySelectValues.value,
        false
      );
    } else if (processFlag.value === 1) {
      form.setFieldValue(
        fieldName("lbl_delivery_group_name"),
        deliveryGroupUpdateInput.name,
        false
      );
    }

    // キャップ数設定
    form.setFieldValue(
      fieldName("lbl_delivery_cap_setting"),
      selectGroupCapSelectValues.value.index,
      false
    );

    if (selectGroupCapSelectValues.value.index === 0) {
      // 全曜日の場合は基本キャップ数を同期
      form.setFieldValue(
        fieldName("lbl_max_deliverable_unit"),
        deliveryGroupCreateInput.max_deliverable_unit,
        false
      );
    } else {
      // 曜日ごとの場合は各曜日のキャップ数を同期
      form.setFieldValue(
        fieldName("lbl_delivery_cap_mon"),
        deliveryGroupCreateInput.group_cap_mon,
        false
      );
      form.setFieldValue(
        fieldName("lbl_delivery_cap_tue"),
        deliveryGroupCreateInput.group_cap_tue,
        false
      );
      form.setFieldValue(
        fieldName("lbl_delivery_cap_wed"),
        deliveryGroupCreateInput.group_cap_wed,
        false
      );
      form.setFieldValue(
        fieldName("lbl_delivery_cap_thu"),
        deliveryGroupCreateInput.group_cap_thu,
        false
      );
      form.setFieldValue(
        fieldName("lbl_delivery_cap_fri"),
        deliveryGroupCreateInput.group_cap_fri,
        false
      );
      form.setFieldValue(
        fieldName("lbl_delivery_cap_sat"),
        deliveryGroupCreateInput.group_cap_sat,
        false
      );
      form.setFieldValue(
        fieldName("lbl_delivery_cap_sun"),
        deliveryGroupCreateInput.group_cap_sun,
        false
      );
    }
  };

  return {
    isGroupFormValid,
    syncGroupFormValues,
  };
};
